import {
  AvatarTrigger,
  DateRangePicker,
  PrimaryButton,
  parseDateFromValue,
} from "@certn/cascadia-react";
import { _ResponsivePopover } from "@certn/cascadia-react/components/_assets/_ResponsivePopover";
import { css } from "@emotion/css";
import { CalendarDate } from "@internationalized/date";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DialogTrigger } from "react-aria-components";

import { formatMonitoringDateRangeLabel } from "../monitoringFormat";

interface Props {
  dateRange: string | null;
  onChange: (dateRange: string | null) => void;
}

function getRangeValueFromParam(
  dateRange: string | null
): { start: CalendarDate; end: CalendarDate } | null {
  if (!dateRange) {
    return null;
  }
  const [start, end] = dateRange.split(",");
  const parsedValue = parseDateFromValue({ start, end });
  if (!parsedValue || !("start" in parsedValue)) {
    return null;
  }
  return {
    start: new CalendarDate(
      parsedValue.start.year,
      parsedValue.start.month,
      parsedValue.start.day
    ),
    end: new CalendarDate(
      parsedValue.end.year,
      parsedValue.end.month,
      parsedValue.end.day
    ),
  };
}

export function ActivityLogDateFilter({ dateRange, onChange }: Props) {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);
  const [filterLabel, setFilterLabel] = useState(t("Date") ?? "");

  useEffect(() => {
    if (!dateRange) {
      setFilterLabel(t("Date") ?? "");
      return;
    }
    setFilterLabel(formatMonitoringDateRangeLabel(dateRange, t("Date")));
  }, [dateRange, t]);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setOpen}>
      <AvatarTrigger
        label={filterLabel}
        isOpen={isOpen}
        onToggle={() => setOpen((current) => !current)}
        count={dateRange ? 1 : 0}
        selectedItems={[]}
        selectionMode="single"
        icon="calendar_month"
        testID="activity-log-date-filter-trigger"
      />
      <_ResponsivePopover
        isDismissable
        isOpen={isOpen}
        onOpenChange={setOpen}
        popoverPlacement="bottom left"
        popoverMinWidth="280px"
        popoverMaxWidth="320px"
      >
        <DateRangePicker
          label={filterLabel}
          testID="activity-log-date-filter"
          startName="activity-log-date-start"
          endName="activity-log-date-end"
          isStatic
          aria-label={t("Date range picker") ?? ""}
          value={getRangeValueFromParam(dateRange)}
          onChange={(value) => {
            const start = value?.start?.toString();
            const end = value?.end?.toString();
            if (start && end) {
              onChange(`${start},${end}`);
              setOpen(false);
            }
          }}
        />
        <div className={styles.actions}>
          <PrimaryButton
            testID="activity-log-date-filter-clear"
            onPress={() => {
              onChange(null);
              setOpen(false);
            }}
          >
            {t("VERB Clear", "Clear")}
          </PrimaryButton>
        </div>
      </_ResponsivePopover>
    </DialogTrigger>
  );
}

const styles = {
  actions: css({
    display: "flex",
    justifyContent: "flex-end",
    padding: "var(--space-3) var(--space-4)",
  }),
};
